import type { ProcessModelValidationSeverity, TranslationKey } from "@ankaadia/ankaadia-shared";
import type {
  ProcessAreaModelEntityFragment,
  ProcessPhaseModelEntityFragment,
  ProcessStageInfo,
  ProcessTaskModel,
  ProcessTaskModelResultFragment,
  ProcessTaskResult,
} from "@ankaadia/graphql";
import { PrimeIcons } from "primeng/api";
import type { ProcessTaskRelation } from "./process-task-relations.helper";

export const newNodeId = "new";

/**
 * The two ways a process model can be worked on: as a tree, or as a graph. Lives here rather than on
 * the component, so the storage the choice is remembered in can name the type without importing the
 * component that offers it - which would be a cycle, since that component reads the storage.
 */
export type ProcessModelItemViewMode = "Klassisch" | "Grafisch";

// Every process-model entity a task type can be authored as. Derived from the generated ProcessTaskModel
// union's own __typename discriminants (@ankaadia/graphql) rather than hand-listed, so it can never drift
// from the schema — codegen is the only thing that can add/remove a member here.
export type SupportedTaskTypes = NonNullable<ProcessTaskModel["__typename"]>;

export type ProcessTaskModelResultFragmentWithPreviousTaskId = ProcessTaskModelResultFragment & {
  previousTaskId?: string;
};
export type ProcessAreaModelEntityFragmentWithPreviousAreaId = ProcessAreaModelEntityFragment & {
  previousAreaId?: string;
};
export type ProcessPhaseModelEntityFragmentWithPreviousPhaseId = ProcessPhaseModelEntityFragment & {
  previousPhaseId?: string;
};

export type NodeDataModelType =
  | ProcessStageInfo
  | ProcessAreaModelEntityFragment
  | ProcessPhaseModelEntityFragment
  | ProcessTaskModelResultFragment;

export interface INodeData {
  model: NodeDataModelType;
  operations: { label: string; icon: string; action: (x: any, e?: Event) => void }[];
  typeDisplayName: string;
  typeStyleClass: string;
  textStyleClass: string;
  tagStyleClass: string;
  validationMessage?: string;
  validationSeverity?: ProcessModelValidationSeverity;
}

export type ProcessNodeLevel = "process" | "area" | "phase" | "task";

const processTypeNames: ReadonlySet<string> = new Set([
  "ProcessModel",
  "ProcessModelEntity",
  "ProcessModelExtension",
  "ProcessModelExtensionEntity",
  "ProcessModelInfo",
  "ProcessStageInfo",
]);

const areaTypeNames: ReadonlySet<string> = new Set(["ProcessAreaModel", "ProcessAreaModelEntity"]);

const phaseTypeNames: ReadonlySet<string> = new Set(["ProcessPhaseModel", "ProcessPhaseModelEntity"]);

export function getNodeLevel(model: { _typeName?: string; __typename?: string }): ProcessNodeLevel {
  const typeName = model._typeName ?? model.__typename;
  if (processTypeNames.has(typeName)) return "process";
  if (areaTypeNames.has(typeName)) return "area";
  if (phaseTypeNames.has(typeName)) return "phase";
  return "task";
}

export function getNodeTypeStyleClass(model: { _typeName?: string; __typename?: string }): string {
  switch (getNodeLevel(model)) {
    case "process":
      return "pm-tag-indigo";
    case "area":
      return "pm-tag-green";
    case "phase":
      return "pm-tag-purple";
    case "task":
      return "pm-tag-gray";
  }
}

export function getNodeTextStyleClass(model: { _typeName?: string; __typename?: string }): string {
  switch (getNodeLevel(model)) {
    case "process":
      return "pm-font-bold";
    case "area":
      return "pm-font-semibold";
    case "phase":
      return "pm-font-medium";
    case "task":
      return "pm-font-normal";
  }
}

export function getNodeTagStyleClass(model: { _typeName?: string; __typename?: string }): string {
  return `${getNodeTypeStyleClass(model)} ${getNodeTextStyleClass(model)}`;
}

export const taskRelationLabels: Record<ProcessTaskRelation, TranslationKey> = {
  predecessor: "processModel.treeNode.relation.view.predecessor",
  alternative: "processModel.treeNode.relation.view.alternative",
  reference: "processModel.treeNode.relation.view.reference",
  reactivation: "processModel.treeNode.relation.view.reactivation",
};

export const taskRelationTooltips: Record<ProcessTaskRelation, TranslationKey> = {
  predecessor: "processModel.treeNode.relation.predecessor",
  alternative: "processModel.treeNode.relation.alternative",
  reference: "processModel.treeNode.relation.reference",
  reactivation: "processModel.treeNode.relation.reactivation",
};

export const ProcessTaskIcon: Record<SupportedTaskTypes, string> = {
  ProcessTaskAutoOperationModel: PrimeIcons.BOLT,
  ProcessTaskTriggerEventModel: PrimeIcons.WAVE_PULSE,
  ProcessTaskAssignmentExceptionModel: PrimeIcons.EJECT,
  ProcessTaskDeactivateProcessModel: PrimeIcons.BRIEFCASE,
  ProcessTaskDocumentCheckModel: PrimeIcons.FILE_CHECK,
  ProcessTaskEnterMissingInformationModel: PrimeIcons.PEN_TO_SQUARE,
  ProcessTaskInformationCollectionModel: PrimeIcons.FILE_EDIT,
  ProcessTaskInformationSendOutModel: PrimeIcons.SEND,
  ProcessTaskInviteToPlatformModel: PrimeIcons.USER_PLUS,
  ProcessTaskPdfFormGenerationModel: PrimeIcons.FILE_PDF,
  ProcessTaskOnlineApplicationModel: PrimeIcons.WAREHOUSE,
  ProcessTaskUpdateCandidateProfileModel: PrimeIcons.ID_CARD,
  ProcessTaskUploadModel: PrimeIcons.UPLOAD,
  ProcessTaskEmailSendModel: PrimeIcons.ENVELOPE,
  ProcessTaskWorkModel: PrimeIcons.CHECK,
  ProcessTaskReminderModel: PrimeIcons.BELL,
  ProcessTaskRoleSetupModel: PrimeIcons.USERS,
  ProcessTaskOrganizationAuditModel: PrimeIcons.BUILDING,
  ProcessTaskEnterMissingInformationV2Model: PrimeIcons.PEN_TO_SQUARE,
  ProcessTaskTranslateDocumentsModel: PrimeIcons.LANGUAGE,
  ProcessTaskUploadAvatarModel: PrimeIcons.IMAGE,
  ProcessTaskInterviewWriteModel: PrimeIcons.HAMMER,
  ProcessTaskEducationAssignToExamModel: PrimeIcons.GRADUATION_CAP,
  ProcessTaskEducationEnterExamResultModel: PrimeIcons.HEART,
  ProcessTaskSigningModel: PrimeIcons.PENCIL,
};

// The runtime process-task type each SupportedTaskTypes (process-model entity) configures. Derived from the
// generated ProcessTaskResult union's own __typename discriminants (@ankaadia/graphql) rather than hand-listed,
// so it can never drift from the schema — codegen is the only thing that can add/remove a member here.
export type RuntimeTaskType = NonNullable<ProcessTaskResult["__typename"]>;

export function isSupportedTaskType(typeName: string): typeName is SupportedTaskTypes {
  return typeName in runtimeTaskTypeByModelType;
}

// Each SupportedTaskTypes (process-model entity) maps to the RuntimeTaskType it configures. Spelled out
// explicitly, not derived by string manipulation, so TypeScript's exhaustiveness checking (a missing key,
// or a value outside RuntimeTaskType, is a compile error) is what keeps this in sync — not a naming-convention
// assumption.
export const runtimeTaskTypeByModelType: Record<SupportedTaskTypes, RuntimeTaskType> = {
  ProcessTaskAutoOperationModel: "ProcessTaskAutoOperation",
  ProcessTaskTriggerEventModel: "ProcessTaskTriggerEvent",
  ProcessTaskAssignmentExceptionModel: "ProcessTaskAssignmentException",
  ProcessTaskDeactivateProcessModel: "ProcessTaskDeactivateProcess",
  ProcessTaskDocumentCheckModel: "ProcessTaskDocumentCheck",
  ProcessTaskEnterMissingInformationModel: "ProcessTaskEnterMissingInformation",
  ProcessTaskInformationCollectionModel: "ProcessTaskInformationCollection",
  ProcessTaskInformationSendOutModel: "ProcessTaskInformationSendOut",
  ProcessTaskInviteToPlatformModel: "ProcessTaskInviteToPlatform",
  ProcessTaskPdfFormGenerationModel: "ProcessTaskPdfFormGeneration",
  ProcessTaskOnlineApplicationModel: "ProcessTaskOnlineApplication",
  ProcessTaskUpdateCandidateProfileModel: "ProcessTaskUpdateCandidateProfile",
  ProcessTaskUploadModel: "ProcessTaskUpload",
  ProcessTaskEmailSendModel: "ProcessTaskEmailSend",
  ProcessTaskWorkModel: "ProcessTaskWork",
  ProcessTaskReminderModel: "ProcessTaskReminder",
  ProcessTaskRoleSetupModel: "ProcessTaskRoleSetup",
  ProcessTaskOrganizationAuditModel: "ProcessTaskOrganizationAudit",
  ProcessTaskEnterMissingInformationV2Model: "ProcessTaskEnterMissingInformationV2",
  ProcessTaskTranslateDocumentsModel: "ProcessTaskTranslateDocuments",
  ProcessTaskUploadAvatarModel: "ProcessTaskUploadAvatar",
  ProcessTaskInterviewWriteModel: "ProcessTaskInterviewWrite",
  ProcessTaskEducationAssignToExamModel: "ProcessTaskEducationAssignToExam",
  ProcessTaskEducationEnterExamResultModel: "ProcessTaskEducationEnterExamResult",
  ProcessTaskSigningModel: "ProcessTaskSigning",
};
